import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = 'https://ufc-backend-nmib.onrender.com/api';



function Avatar({ name, side, size = 80 }) {
  const initials = (name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const color = side === 'f1' ? 'var(--teal-light)' : 'var(--orange)';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'rgba(255,255,255,0.05)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontFamily: 'DM Serif Display, serif',
      fontStyle: 'italic', color,
      border: '1px solid rgba(255,255,255,0.08)',
    }}>
      {initials}
    </div>
  );
}

// Fixed full-height side hero — lives outside scroll flow
function SideHero({ fighter, side, onClear, onSearchClick }) {
  const [imgErr, setImgErr] = useState(false);
  const isF1    = side === 'f1';
  const hasImg  = fighter && fighter.image_url && fighter.image_url.length > 5 && !imgErr;
  const accent  = isF1 ? 'var(--teal-light)' : 'var(--orange)';
  const glowRgb = isF1 ? '13,148,136' : '194,70,10';
  const label   = isF1 ? 'FIGHTER 1' : 'FIGHTER 2';

  return (
    <div
      className={`cs-hero-anchor ${side}`}
      style={{ '--glow': `rgba(${glowRgb},0.4)` }}
    >
      {/* Radial glow */}
      <div className="cs-hero-glow" />

      {/* Fighter image */}
      {hasImg ? (
        <img
          src={fighter.image_url}
          alt={fighter?.full_name || ''}
          className={`cs-hero-img ${isF1 ? '' : 'flip'}`}
          onError={() => setImgErr(true)}
        />
      ) : fighter ? (
        <div className="cs-hero-avatar-wrap">
          <Avatar name={fighter.full_name} side={side} size={120} />
        </div>
      ) : null}

      {/* Seamless fade to center */}
      <div className={`cs-hero-side-fade ${side}`} />

      {/* Empty click target */}
      {!fighter && (
        <div className="cs-hero-empty-overlay" onClick={onSearchClick}>
          <div className="cs-hero-empty-prompt" style={{ color: accent }}>SELECT</div>
          <div className="cs-hero-empty-sub">Click to search</div>
        </div>
      )}

      {/* Fighter info */}
      {fighter && (
        <div className={`cs-hero-info ${side}`} style={{ pointerEvents: 'auto' }}>
          <div className="cs-hero-label" style={{ color: accent }}>{label}</div>
          <div className="cs-hero-name">{fighter.full_name}</div>
          <div className="cs-hero-record" style={{ color: accent }}>
            {fighter.wins}-{fighter.losses}
          </div>
          {fighter.style && <div className="cs-hero-style">{fighter.style}</div>}
          <button className="cs-hero-change" onClick={onClear}>Change</button>
        </div>
      )}
    </div>
  );
}

function FighterTile({ fighter, isF1Selected, isF2Selected, isSuggested, onClick }) {
  const [imgErr, setImgErr] = useState(false);
  const hasImg = fighter.image_url && fighter.image_url.length > 5 && !imgErr;
  const side   = fighter._side;

  const suggestedClass = isSuggested === 'f1' ? 'sug-f1' : isSuggested === 'f2' ? 'sug-f2' : '';

  return (
    <div
      className={`cs-tile ${isF1Selected ? 'sel-f1' : ''} ${isF2Selected ? 'sel-f2' : ''} ${suggestedClass}`}
      onClick={() => onClick(fighter)}
      title={`${fighter.full_name} (${fighter.wins}-${fighter.losses})`}
    >
      {hasImg ? (
        <img
          src={fighter.image_url}
          alt={fighter.full_name}
          className="cs-tile-img"
          onError={() => setImgErr(true)}
        />
      ) : (
        <Avatar name={fighter.full_name} side={side} size={40} />
      )}
      {(isF1Selected || isF2Selected) && (
        <div className={`cs-tile-badge ${isF1Selected ? 'f1' : 'f2'}`}>
          {isF1Selected ? '1' : '2'}
        </div>
      )}
      <div className="cs-tile-name">
        {fighter.full_name}
        <span className="cs-tile-record">{fighter.wins}-{fighter.losses}</span>
      </div>
    </div>
  );
}

function SearchOverlay({ side, onSelect, onClose }) {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [status,  setStatus]  = useState('');  // 'searching' | 'done' | 'error'
  const inputRef  = useRef(null);
  const timerRef  = useRef(null);
  const color     = side === 'f1' ? 'var(--teal-light)' : 'var(--orange)';

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  function handleChange(e) {
    const val = e.target.value;
    setQuery(val);

    clearTimeout(timerRef.current);

    if (val.length < 2) {
      setResults([]);
      setStatus('');
      return;
    }

    setStatus('searching');
    timerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/fighters/search`, {
          params: { q: val },
          timeout: 15000,   // 15s — enough for Render cold start
        });
        const data = res.data;
        setResults(Array.isArray(data) ? data : []);
        setStatus('done');
      } catch (err) {
        console.error('Search error:', err?.message, err?.response?.status);
        setResults([]);
        // Show "waking up" message if it looks like a cold start timeout
        setStatus(err?.code === 'ECONNABORTED' ? 'waking' : 'error');
      }
    }, 350);
  }

  return (
    <div className="cs-search-overlay" onClick={onClose}>
      <div className="cs-search-box" onClick={e => e.stopPropagation()}>
        <div className="cs-search-title" style={{ color }}>
          {side === 'f1' ? 'Select Fighter 1' : 'Select Fighter 2'}
        </div>
        <input
          ref={inputRef}
          className="cs-search-input"
          placeholder="Search any UFC fighter..."
          value={query}
          onChange={handleChange}
        />
        {status === 'searching' && (
          <div className="cs-search-loading">Searching...</div>
        )}
        {status === 'error' && (
          <div className="cs-search-loading" style={{ color: 'var(--orange)' }}>
            Search failed — backend may be waking up, try again in 30s
          </div>
        )}
        {status === 'waking' && (
          <div className="cs-search-loading" style={{ color: 'var(--teal-light)' }}>
            App is waking up 😴 — try again in 30 seconds
          </div>
        )}
        {status === 'done' && results.length === 0 && (
          <div className="cs-search-loading">No fighters found</div>
        )}
        <div className="cs-search-results">
          {results.map(f => (
            <div key={f.fighter_id} className="cs-search-result" onClick={() => onSelect(f)}>
              <span className="cs-search-name">{f.full_name}</span>
              <span className="cs-search-meta">
                {f.latest_weight_class} · {f.wins}W {f.losses}L
              </span>
            </div>
          ))}
        </div>
        <button className="cs-search-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

export default function Home() {
  const [events,      setEvents]      = useState([]);
  const [activeEvent, setActiveEvent] = useState(0);
  const [fighter1,    setFighter1]    = useState(null);
  const [fighter2,    setFighter2]    = useState(null);
  const [searchFor,   setSearchFor]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/fighters/upcoming`)
      .then(r => { setEvents(r.data.events || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  function handleTileClick(fighter) {
    if (fighter1?.fighter_id === fighter.fighter_id) { setFighter1(null); return; }
    if (fighter2?.fighter_id === fighter.fighter_id) { setFighter2(null); return; }
    if (!fighter1) { setFighter1(fighter); return; }
    if (!fighter2) { setFighter2(fighter); return; }
    setFighter2(fighter);
  }

  const currentEvent = events[activeEvent];
  const allFighters  = currentEvent
    ? currentEvent.bouts.flatMap(b => [
        { ...b.fighter1, _side: 'f1' },
        { ...b.fighter2, _side: 'f2' },
      ])
    : [];

  // Build a map of fighter_id -> opponent fighter_id for this event's bouts
  const opponentMap = {};
  if (currentEvent) {
    currentEvent.bouts.forEach(b => {
      opponentMap[b.fighter1.fighter_id] = b.fighter2.fighter_id;
      opponentMap[b.fighter2.fighter_id] = b.fighter1.fighter_id;
    });
  }

  // Determine which fighters should be "suggested" (the opponent of the currently selected)
  const suggestedF1Opp = fighter1 ? opponentMap[fighter1.fighter_id] : null;
  const suggestedF2Opp = fighter2 ? opponentMap[fighter2.fighter_id] : null;

  return (
    <div className="cs-root">

      {/* Search overlay */}
      {searchFor && (
        <SearchOverlay
          side={searchFor}
          onSelect={f => {
            if (searchFor === 'f1') setFighter1(f);
            else setFighter2(f);
            setSearchFor(null);
          }}
          onClose={() => setSearchFor(null)}
        />
      )}

      {/* Fixed side heroes — behind everything */}
      <SideHero fighter={fighter1} side="f1"
        onClear={() => setFighter1(null)}
        onSearchClick={() => setSearchFor('f1')} />
      <SideHero fighter={fighter2} side="f2"
        onClear={() => setFighter2(null)}
        onSearchClick={() => setSearchFor('f2')} />

      {/* Scrollable center content — floats above heroes */}
      <div className="cs-center">

        <div className="cs-header">
          <div className="cs-logo">
            <span className="cs-logo-ufc">UFC</span>
            <span className="cs-logo-x">✕</span>
            <span className="cs-logo-predict">PREDICTOR</span>
          </div>
          <div className="cs-choose">CHOOSE YOUR FIGHTER</div>
        </div>

        <div className="cs-action-row">
          <button className="cs-search-btn f1" onClick={() => setSearchFor('f1')}>
            + Search F1
          </button>
          <button
            className="cs-compare-btn"
            disabled={!fighter1 || !fighter2}
            onClick={() => navigate(`/matchup?f1=${fighter1.fighter_id}&f2=${fighter2.fighter_id}`)}
          >
            {fighter1 && fighter2 ? 'ANALYZE' : 'VS'}
          </button>
          <button className="cs-search-btn f2" onClick={() => setSearchFor('f2')}>
            Search F2 +
          </button>
        </div>

        {events.length > 0 && (
          <div className="cs-event-tabs">
            {events.slice(0, 4).map((ev, i) => (
              <button
                key={ev.event_id}
                className={`cs-event-tab ${i === activeEvent ? 'active' : ''}`}
                onClick={() => setActiveEvent(i)}
              >
                <span className="cs-event-tab-name">
                  {(ev.event_name || '').replace('UFC ', '').replace('Fight Night: ', '').slice(0, 22)}
                </span>
                <span className="cs-event-tab-date">
                  {ev.event_date
                    ? new Date(ev.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : ''}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="cs-grid-wrap">
          {loading && <div className="cs-loading">Waking Up App 😴</div>}

          {!loading && currentEvent && (
            <>
              <div className="cs-event-title">
                {currentEvent.event_name}
                {currentEvent.location && (
                  <span className="cs-event-location"> — {currentEvent.location}</span>
                )}
              </div>
              <div className="cs-grid">
                {allFighters.map((fighter, i) => {
                  const isF1Sel = fighter1?.fighter_id === fighter.fighter_id;
                  const isF2Sel = fighter2?.fighter_id === fighter.fighter_id;
                  // Suggest opponent only if other slot is empty
                  const isSuggestedForF2 = !fighter2 && suggestedF1Opp === fighter.fighter_id;
                  const isSuggestedForF1 = !fighter1 && suggestedF2Opp === fighter.fighter_id;
                  return (
                    <FighterTile
                      key={`${fighter.fighter_id}-${i}`}
                      fighter={fighter}
                      isF1Selected={isF1Sel}
                      isF2Selected={isF2Sel}
                      isSuggested={isSuggestedForF1 ? 'f1' : isSuggestedForF2 ? 'f2' : null}
                      onClick={handleTileClick}
                    />
                  );
                })}
              </div>
              <div className="cs-grid-hint">
                Click to select · Click again to deselect · Use Search for any UFC fighter
              </div>
            </>
          )}

          {!loading && events.length === 0 && (
            <div className="cs-no-events">
              No upcoming events. Use the search buttons to compare any two fighters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
